import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import type { Emphasis, Root, Text } from 'mdast';
import { $remark } from '@milkdown/kit/utils';
import { SKIP, visit } from 'unist-util-visit';

/**
 * CommonMark flanking: after `**bold**`, leftover `*.*` cannot open emphasis
 * (`*` between a letter and punctuation is not left-flanking).
 * Platform / ProseMirror serializers still emit that glued form.
 *
 * Rewrite sibling text `\*inner\*` after `strong` / `emphasis` into an `emphasis` node.
 */
const GLUED_STAR_EMPHASIS = /^\*([^*]+)\*(.*)$/s;
const SKIP_PARENT_TYPES = new Set(['code', 'inlineCode']);

type PhrasingParent = { type?: string; children?: Array<{ type?: string; value?: string; children?: unknown[] }> };

export function transformGluedStarEmphasis(tree: Root): void {
  visit(tree, (node) => {
    if (SKIP_PARENT_TYPES.has((node as { type?: string }).type ?? '')) {
      return SKIP;
    }

    const parent = node as PhrasingParent;
    const children = parent.children;
    if (!Array.isArray(children)) return;

    let i = 1;
    while (i < children.length) {
      const prev = children[i - 1];
      const cur = children[i];
      if ((prev.type === 'strong' || prev.type === 'emphasis') && cur.type === 'text') {
        const match = (cur.value ?? '').match(GLUED_STAR_EMPHASIS);
        if (match && match[1].trim()) {
          const emphasis: Emphasis = {
            type: 'emphasis',
            children: [{ type: 'text', value: match[1] } satisfies Text],
          };
          const next: PhrasingParent['children'] = [emphasis];
          if (match[2]) {
            next.push({ type: 'text', value: match[2] });
          }
          children.splice(i, 1, ...next);
          continue;
        }
      }
      i += 1;
    }
  });
}

export const remarkGluedStarEmphasis = $remark('brainfish-glued-star-emphasis', () => {
  return () => (tree: Root) => {
    transformGluedStarEmphasis(tree);
  };
});

export const gluedStarEmphasisPlugin: MilkdownPlugin[] = [remarkGluedStarEmphasis].flat();
